import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Rating,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import {
  deleteTestimonial,
  saveTestimonial,
} from '../../../redux/features/landingCmsSlice';

/**
 * The video testimonials.
 *
 * ---------------------------------------------------------------------------
 * THE FIELD IS "PASTE THE LINK", NOT "ENTER THE VIDEO ID"
 *
 * The database stores a bare YouTube id, because the component builds both the
 * thumbnail and the embed URL from it. But nobody has a bare id — they have the
 * link from the address bar, and pasting it is what everyone does.
 *
 * So the server extracts the id from a watch URL, a youtu.be link, an embed
 * link, a Shorts link or a bare id, and only rejects things that are not
 * YouTube at all. Rejecting the URL would be technically correct and
 * practically hostile, and the failure it causes is silent — a broken thumbnail
 * and a dead play button, with no error anywhere.
 *
 * The thumbnail renders here as soon as the id resolves, which is the fastest
 * possible confirmation that the right video was pasted.
 */

const emptyDraft = { videoId: '', studentName: '', roleLabel: '', rating: 5, isVisible: true };

const TestimonialsPanel = ({ testimonials, revalidateSeconds, onChanged }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.landingCms);

  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const save = async (draft) => {
    try {
      await dispatch(saveTestimonial(draft)).unwrap();
      enqueueSnackbar(
        `Testimonial saved. Live within ${revalidateSeconds} seconds.`,
        { variant: 'success', autoHideDuration: 6000 }
      );
      setEditing(null);
      onChanged();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not save the testimonial', { variant: 'error' });
    }
  };

  const remove = async () => {
    try {
      await dispatch(deleteTestimonial(confirmDelete.id)).unwrap();
      enqueueSnackbar('Testimonial removed', { variant: 'success' });
      setConfirmDelete(null);
      onChanged();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not remove the testimonial', { variant: 'error' });
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Testimonials</Typography>
          <Typography variant="caption" color="text.secondary">
            The video cards in the &quot;What our students say&quot; band.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Icon icon="mdi:plus" width={16} />}
          onClick={() => setEditing({ ...emptyDraft })}
        >
          Add testimonial
        </Button>
      </Stack>

      {testimonials.length === 0 && (
        <Alert severity="info">
          No testimonials yet. The section will fall back to the three built-in videos.
        </Alert>
      )}

      <Grid container spacing={2}>
        {testimonials.map((t) => (
          <Grid item xs={12} sm={6} md={4} key={t.id}>
            <Card sx={{ opacity: t.isVisible ? 1 : 0.55 }}>
              <Box
                component="img"
                src={t.thumbnailUrl}
                alt=""
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  objectFit: 'cover',
                  display: 'block',
                  bgcolor: 'background.neutral',
                }}
              />
              <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t.studentName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t.roleLabel || '—'}
                </Typography>
                <Rating value={t.rating} readOnly size="small" sx={{ mt: 0.5 }} />
                <Stack direction="row" alignItems="center" sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t.videoId}
                    sx={{ height: 20, fontFamily: 'monospace', fontSize: 11 }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => setEditing({ ...t })}>
                      <Icon icon="mdi:pencil-outline" width={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => setConfirmDelete(t)}>
                      <Icon icon="mdi:trash-can-outline" width={18} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {editing && (
        <TestimonialDialog
          draft={editing}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove this testimonial?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDelete?.studentName} — {confirmDelete?.roleLabel}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            To take it off the page temporarily, switch it to hidden instead.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={remove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/** YouTube id from anything anyone might paste. Mirrors the server's extractor. */
const extractId = (input) => {
  const raw = String(input || '').trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(raw)) return raw;
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/live\/([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = raw.match(p);
    if (m) return m[1];
  }
  return null;
};

const TestimonialDialog = ({ draft, saving, onClose, onSave }) => {
  const [form, setForm] = useState(draft);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const resolvedId = extractId(form.videoId);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{draft.id ? 'Edit testimonial' : 'Add a testimonial'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="YouTube link"
            placeholder="https://www.youtube.com/watch?v=..."
            value={form.videoId}
            onChange={(e) => set({ videoId: e.target.value })}
            error={Boolean(form.videoId) && !resolvedId}
            helperText={
              !form.videoId
                ? 'Paste the video link from YouTube.'
                : resolvedId
                ? `Video id: ${resolvedId}`
                : 'That does not look like a YouTube video.'
            }
          />

          {/* The fastest possible confirmation that the right video was pasted. */}
          {resolvedId && (
            <Box
              component="img"
              src={`https://i.ytimg.com/vi/${resolvedId}/hqdefault.jpg`}
              alt=""
              sx={{
                width: '100%',
                aspectRatio: '16 / 9',
                objectFit: 'cover',
                borderRadius: 1,
                bgcolor: 'background.neutral',
              }}
            />
          )}

          <TextField
            fullWidth
            size="small"
            label="Name"
            value={form.studentName}
            onChange={(e) => set({ studentName: e.target.value })}
            helperText="Shown under the video."
          />
          <TextField
            fullWidth
            size="small"
            label="Role"
            placeholder="Parent review"
            value={form.roleLabel || ''}
            onChange={(e) => set({ roleLabel: e.target.value })}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Rating
            </Typography>
            <Rating
              value={Number(form.rating) || 5}
              onChange={(_, v) => set({ rating: v || 5 })}
            />
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={form.isVisible !== false}
              onChange={(e) => set({ isVisible: e.target.checked })}
            />
            <Typography variant="body2">Show on the page</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={saving || !resolvedId || !form.studentName}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestimonialsPanel;
